import { ZoomIn, ZoomOut } from 'lucide-react'
import React, { useCallback, useEffect } from 'react'
import {
    ReactFlow,
    Node,
    Edge,
    useNodesState,
    useEdgesState,
    Position,
    MarkerType,
    ReactFlowProvider,
    useReactFlow,
    Handle,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { FamilyMember } from '../types'
import { Tooltip as ReactTooltip } from 'react-tooltip'

interface FamilyTreeProps {
    data: FamilyMember | null
    loading: boolean
    error: string | null
}

// 自定义节点组件
const FamilyTreeNode = ({ data }: { data: { member: FamilyMember } }) => {
    const { member } = data

    const getMemberClasses = () => {
        let classes = 'member'
        if (member.is_main_line) {
            classes += ' main-line'
        }
        return classes
    }

    return (
        <div
            className={getMemberClasses()}
            data-tooltip-id="family-tooltip"
            data-tooltip-html={member.notes?.join('<br />')}
            title={member.is_main_line ? `${member.name} (主线)` : member.name}
            style={{
                border: '2px solid var(--line-color)',
                padding: '8px 16px',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                color: 'var(--dark-text)',
                fontSize: '14px',
                borderRadius: '8px',
                minWidth: '80px',
                textAlign: 'center',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                ...(member.is_main_line && {
                    borderColor: 'var(--main-red)',
                    fontWeight: 'bold',
                    color: 'var(--main-red)',
                }),
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--main-red)'
                e.currentTarget.style.color = 'white'
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.1)'
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                    'rgba(255, 255, 255, 0.7)'
                e.currentTarget.style.color = member.is_main_line
                    ? 'var(--main-red)'
                    : 'var(--dark-text)'
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow =
                    '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
        >
            <Handle
                type="target"
                position={Position.Top}
                style={{
                    background: '#a11d20',
                    border: '2px solid white',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    opacity: 0.8,
                }}
                isConnectable={true}
            />
            <span className="member-name">{member.name}</span>
            {member.is_main_line && (
                <span
                    className="main-line-indicator"
                    aria-label="主线"
                    style={{
                        color: 'var(--main-red)',
                        fontSize: '12px',
                        marginLeft: '4px',
                        verticalAlign: 'middle',
                    }}
                >
                    ●
                </span>
            )}
            <Handle
                type="source"
                position={Position.Bottom}
                style={{
                    background: '#a11d20',
                    border: '2px solid white',
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    opacity: 0.8,
                }}
                isConnectable={true}
            />
        </div>
    )
}

// 节点类型定义
const nodeTypes = {
    familyMember: FamilyTreeNode,
}

// 内部组件，可以使用 useReactFlow
const FamilyTreeFlow: React.FC<FamilyTreeProps> = ({
    data,
    loading,
    error,
}) => {
    const reactFlowInstance = useReactFlow()
    const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[])
    const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[])

    // 手机端默认 scale 为 0.7，桌面端为 1
    // const getInitialScale = () => {
    //     return typeof window !== 'undefined' && window.innerWidth < 1024
    //         ? 1.5
    //         : 2
    // }

    // 将家谱数据转换为节点和边
    const convertToNodesAndEdges = useCallback((rootMember: FamilyMember) => {
        const nodes: Node[] = []
        const edges: Edge[] = []
        let nodeId = 0

        // 计算节点位置的函数
        const calculateLayout = (
            member: FamilyMember,
            level: number = 0,
            parentX: number = 0,
            parentId?: string,
        ): string => {
            const nodeSpacing = 150 // 节点间距
            const levelSpacing = 100 // 层级间距
            const id = `node-${nodeId++}`

            // 计算子节点的总宽度
            const childrenCount = member.children?.length || 0
            const totalChildrenWidth = Math.max(
                0,
                (childrenCount - 1) * nodeSpacing,
            )

            // 当前节点位置
            let x = parentX
            if (childrenCount > 0) {
                // 如果有子节点，将当前节点放在子节点的中央上方
                x = parentX
            }

            const y = level * levelSpacing

            // 创建节点
            nodes.push({
                id,
                type: 'familyMember',
                position: { x, y },
                data: { member },
                sourcePosition: Position.Bottom,
                targetPosition: Position.Top,
            })

            // 如果有父节点，创建边
            if (parentId) {
                edges.push({
                    id: `edge-${parentId}-${id}`,
                    source: parentId,
                    target: id,
                    type: 'smoothstep',
                    style: {
                        stroke: 'var(--line-color)',
                        strokeWidth: 1, //member.is_main_line ? 2 : 1,
                        opacity: 0.1,
                    },
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        color: 'var(--line-color)',
                        width: 4,
                        height: 4,
                    },
                    animated: false,
                    selectable: false,
                })
            }

            // 如果有子节点，递归处理
            if (member.children && member.children.length > 0) {
                const startX = x - totalChildrenWidth / 2

                member.children.forEach((child, index) => {
                    const childX = startX + index * nodeSpacing
                    calculateLayout(child, level + 1, childX, id)
                })
            }

            return id
        }

        calculateLayout(rootMember, 0, 0)

        return { nodes, edges }
    }, [])

    // 当数据变化时更新节点和边
    useEffect(() => {
        if (data) {
            const { nodes: newNodes, edges: newEdges } =
                convertToNodesAndEdges(data)
            // console.log('Generated nodes:', newNodes.length)
            // console.log('Generated edges:', newEdges.length)
            // console.log('Edges:', newEdges)
            setNodes(newNodes)
            setEdges(newEdges)

            // 延迟执行fitView以确保节点已渲染
            setTimeout(() => {
                reactFlowInstance.fitView({
                    padding: 0.2,
                    minZoom:
                        typeof window !== 'undefined' &&
                        window.innerWidth < 1024
                            ? 0.7
                            : 0.9,
                    maxZoom: 3,
                })
                setTimeout(() => {
                    reactFlowInstance.setViewport({
                        y: 10,
                        x: reactFlowInstance.getViewport().x,
                        zoom: reactFlowInstance.getZoom(),
                    })
                }, 100)
                // reactFlowInstance.zoomTo(2)
                // console.log('zoom', reactFlowInstance.getZoom())
            }, 100)
        }
    }, [data, convertToNodesAndEdges, setNodes, setEdges, reactFlowInstance])

    const handleZoomIn = () => {
        reactFlowInstance.zoomIn()
    }

    const handleZoomOut = () => {
        reactFlowInstance.zoomOut()
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="loading-spinner"></div>
                <p className="ml-4 text-lg text-gray-600">加载族谱数据中...</p>
            </div>
        )
    }

    if (error) {
        return (
            <div className="text-center py-12">
                <p className="text-red-600 text-lg">{error}</p>
            </div>
        )
    }

    if (!data) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-600 text-lg">暂无族谱数据</p>
            </div>
        )
    }

    return (
        <div className="relative">
            <div
                aria-hidden="true"
                className="absolute top-[50px] z-10 left-8 h-[400px] pointer-events-none lg:block"
            >
                <p
                    className="text-gray-600/30 text-xl"
                    style={{ writingMode: 'vertical-rl' }}
                >
                    骏马匆匆出异乡，任从随处立纲常，年深外境犹吾境，日久他乡即故乡。
                </p>
            </div>
            <div
                aria-hidden="true"
                className="absolute top-[50px] z-10 right-8 h-[400px] pointer-events-none lg:block"
            >
                <p
                    className="text-gray-600/30 text-xl"
                    style={{ writingMode: 'vertical-rl' }}
                >
                    朝夕莫忘亲命语，晨昏须荐祖宗香，唯愿苍天垂庇佑，三七男儿总炽昌。
                </p>
            </div>

            <div className="absolute top-4 right-4 z-10 flex space-x-2">
                <button
                    onClick={handleZoomIn}
                    className="bg-[rgba(233,226,207,0.7)] hover:bg-[rgba(186,181,166,0.7)] text-gray-800 font-bold p-2 rounded-full"
                    aria-label="放大"
                >
                    <ZoomIn size={16} />
                </button>
                <button
                    onClick={handleZoomOut}
                    className="bg-[rgba(233,226,207,0.7)] hover:bg-[rgba(186,181,166,0.7)] text-gray-800 font-bold p-2 rounded-full"
                    aria-label="缩小"
                >
                    <ZoomOut size={16} />
                </button>
            </div>

            <section
                id="family-tree-section"
                className="bg-parchment rounded-lg shadow-xl fade-in-up"
                style={{ height: '600px' }}
            >
                <div style={{ width: '100%', height: '100%' }}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        nodeTypes={nodeTypes}
                        defaultViewport={{
                            x: 300,
                            y: 50,
                            zoom:
                                typeof window !== 'undefined' &&
                                window.innerWidth < 1024
                                    ? 1.5
                                    : 2,
                        }}
                        fitView
                        fitViewOptions={{
                            padding: 0.2,
                            minZoom: 0.5,
                            maxZoom: 2.5,
                        }}
                        attributionPosition="bottom-left"
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={true}
                        panOnDrag={true}
                        zoomOnScroll={true}
                        zoomOnPinch={true}
                        zoomOnDoubleClick={false}
                        deleteKeyCode={null}
                        style={{
                            background: 'transparent',
                        }}
                        // connectionLineStyle={{
                        //     stroke: '#a11d20',
                        //     strokeWidth: 1,
                        // }}
                        // defaultEdgeOptions={{
                        //     type: 'smoothstep',
                        //     animated: false,
                        //     style: {
                        //         stroke: '#a11d20',
                        //         strokeWidth: 1,
                        //         opacity: 1,
                        //     },
                        //     markerEnd: {
                        //         type: MarkerType.ArrowClosed,
                        //         color: '#a11d20',
                        //     },
                        // }}
                    />
                </div>
            </section>
            <ReactTooltip id="family-tooltip" />
        </div>
    )
}

// 主组件，包装在 ReactFlowProvider 中
const FamilyTree: React.FC<FamilyTreeProps> = (props) => {
    return (
        <ReactFlowProvider>
            <FamilyTreeFlow {...props} />
        </ReactFlowProvider>
    )
}

export default FamilyTree
